import { isFulfilled } from "@reduxjs/toolkit";
import { useFormik } from "formik";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ERROR_MESSAGES } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { TAgreeTermsValues } from "../../../models/Onboarding/onboarding";
import { Roles } from "../../../models/roles";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../store";
import { hideTransparentPreloaderAction, showTransparentPreloaderAction } from "../../../store/application/slice";
import { AgreeTermsThunk } from "../../../store/onboarding/thunk";
import { TCompletionStatus } from "../../../store/onboarding/types";
import { getCandidateData } from "../../../store/superadmin/thunks";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import CustomButton from "../../UI/Buttons/CustomButton";
import Download from "../../UI/Icons/Download";
import RightArrow from "../../UI/Icons/RightArrow";
import Checkbox from "../../UI/Inputs/Checkbox";

const CasualAndFairWorkStatement: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isImpersonating } = useImpersonation();

    const inProgress = useSelector(
        (state) => state.onboarding.inProgress
    );
    const profileData = useSelector(
        (state) => state.onboarding.userpersonaldetails
    );
    const stepCompletion: TCompletionStatus | null = useSelector(
        (state) => state.onboarding.completedstatus
    );
    // const personalDetails = useSelector((state) => state.onboarding.personaldetails);
    const reviewCandidateData = useSelector((state) => state.superadmin.reviewCandidateData)

    const user = useSelector((state) => state.auth.user);
    
    // Check if the logged-in user is a Super Admin (used for editing candidate profiles)
    const isSuperAdmin =
        (user?.role?.name as Roles) === Roles.SUPER_ADMIN ||
        (user?.role?.name as Roles) === Roles.GENERAL_ADMIN;

    const personalDetails = useSelector(
        (state) =>
        isSuperAdmin ? state.superadmin.reviewCandidateData?.profile :
        state.onboarding.personaldetails
    );

    const currentStage = personalDetails?.current_stage ? personalDetails?.current_stage : null;

    const getAcceptTerms = () => {
        if (personalDetails && 'accept_terms' in personalDetails) {
            return personalDetails.accept_terms ?? false;
        }
        return false;
    };

    const [acceptTerms, setAcceptTerms] = useState<boolean>(getAcceptTerms());
    const [isDisabled, setIsDisabled] = useState<boolean>(true);

    useEffect(() => {
        if(!acceptTerms) {
            setIsDisabled(true);
        } else {
            setIsDisabled(false);
        }
    }, [acceptTerms])

    /**
     * Handles the download of the contract PDF file.
     *
     * This function creates a temporary anchor element, sets its `href` attribute to the provided PDF URL,
     * and triggers a download by simulating a click on the anchor element. After the download is initiated,
     * the anchor element is removed from the DOM.
     *
     * @param {string} type - it denotes which pdf needs to be download.
     */
    const handleDownloadPdf = (type: string) => {
        const link = document.createElement("a");
        let name = 'Fair Work Statement PDF.pdf'
        let pdfUrl = profileData?.employment_policy_url_2;
        if(type === "casual_employee_statement") {
            name = 'Casual Employee Information Statement PDF.pdf'
            pdfUrl = profileData?.employment_policy_url_1;
        }
        link.href = pdfUrl ?? "";
        link.target = "_blank";
        link.setAttribute("download", name); // Ensure the download attribute is set
        document.body.appendChild(link); // Append the link to the DOM
        link.click(); // Trigger the download
        document.body.removeChild(link); // Clean up by removing the link
    };

    const handleChange = (checked: boolean) => {
        setAcceptTerms(checked);
    };

    /** Submit the form for casual and fair work statement */
    const { handleSubmit, setFieldValue } = useFormik({
        validationSchema: createValidationSchema("casualAndFairWorkStatement"),
        validateOnBlur: false,
        validateOnChange: false,
        initialValues: {
            accept_terms: personalDetails !== null && personalDetails?.accept_terms ? personalDetails.accept_terms : false,
        },
        onSubmit: async (values: TAgreeTermsValues) => {
            const payload = { ...values };
            payload.candidate_uuid = isSuperAdmin ? reviewCandidateData?.uuid || "" : "";
            try {
                dispatch(showTransparentPreloaderAction())
                const result = await dispatch(AgreeTermsThunk(payload));
                if(isFulfilled(result)){
                    const candidateResponse = await dispatch(
                                        getCandidateData({ candidate_id: reviewCandidateData?.uuid || "" })
                                      );
                    isSuperAdmin ? navigate("/admin/candidate-profile/next-of-kin") : navigate("/candidate/my-profile/bank-account")
                }
                dispatch(hideTransparentPreloaderAction());
            } catch (error) {
                throw new Error(ERROR_MESSAGES.CONNECTION_ERROR);
            }
        },
    });

    useEffect(() => {
        setFieldValue('accept_terms', acceptTerms)
    }, [setFieldValue, acceptTerms])

    // Get the review status for the current profile
    const reviewStatus = personalDetails?.verification_status;

    /**
     * Determines if a field should be disabled based on status and user role
     * - Super Admin during impersonation: Can ALWAYS edit (any status)
     * - Candidate with "verified" status: CANNOT edit (completed and approved)
     * - Candidate with "waiting" status: CAN edit (submitted for review)
     * - Candidate with null status: CAN edit (not yet submitted)
     */
    const shouldFieldBeDisabled = () => {
        // Super Admin during impersonation can always edit
        if (isImpersonating) {
            return false;
        }
        
        // For candidates: only disable if status is "verified" (completed and approved)
        // Allow editing during "waiting" (submitted for review) and null (not submitted)
        return reviewStatus === 'verified';
    };

    const checkStageSubmittedOrNot = (stageNumber: number | null) => {
        return shouldFieldBeDisabled();
    }

    const handleNext = () => {
        navigate(isSuperAdmin ? "/admin/candidate-profile/next-of-kin" : "/candidate/my-profile/bank-account");
    }

    return (
        
            <div className="d-flex flex-column gap-20 fw-400 text-black">
            <div className="fs-30">Casual & Fair Work Statement</div>
            <div className="fs-16">Please download the below documents for your records.</div>
                <div className="d-flex flex-column gap-10">
                    <div className="d-flex">
                        <CustomButton
                            variant={"primary"}
                            type="button"
                            className="btn-theme bg-teal text-white"
                            onClick={() => handleDownloadPdf('casual_employee_statement')}
                        >
                            <Download />
                            <div className="fs-16 fw-400 text-black text-start">Casual Employee Information Statement PDF</div>
                        </CustomButton>
                    </div>
                </div>
                <div className="d-flex flex-column gap-10">
                    <div className="d-flex">
                        <CustomButton
                            variant={"primary"}
                            type="button"
                            className="btn-theme bg-teal text-white"
                            onClick={() => handleDownloadPdf('fair_work_statement')}
                        >
                            <Download />
                            <div className="fs-16 fw-400 text-black text-start">Fair Work Statement PDF</div>
                        </CustomButton>
                    </div>
                </div>
                <div className="d-flex flex-column gap-10">
                    <div className="d-flex">
                        <div className="theme-check">
                            <Checkbox
                                className="theme-check-input fs-16 fw-400"
                                id="accept_terms"
                                checked={acceptTerms}
                                onChange={handleChange}
                                label={"I have read and understood the Casual Employment Information Statement and Fair Work Statement."}
                                themeCheck={true}
                                disabled={shouldFieldBeDisabled()}
                            />
                        </div>
                    </div>
                </div>
                <div className="h-10px"></div>
                { (stepCompletion?.casual_fair_work_statement === 1 && checkStageSubmittedOrNot(3)) ? 
                    <div className="d-flex">
                        <CustomButton
                            type="submit"
                            variant="primary"
                            onClick={handleNext}
                            className="btn-theme bg-teal text-black"
                        >
                            Next
                            <RightArrow color={"#000000"} />
                        </CustomButton>
                    </div> : 
                    <div className="d-flex flex-column flex-lg-row gap-20">
                        <CustomButton
                            isLoading={inProgress}
                            disabled={isDisabled}
                            contentClass="d-flex"
                            type="submit"
                            variant="primary"
                            className="btn-theme bg-teal text-black"
                            onClick={handleSubmit}
                        >
                            Save and Continue
                            <RightArrow color={(isDisabled || inProgress) ? "#aaaaaa" : "#000000"} />
                        </CustomButton>
                    </div>
                }
            </div>
        
    )
}

export default CasualAndFairWorkStatement;